/**
 * Datasource local User: abstração + implementação localforage.
 */

import localforage from 'localforage';
import { User } from '@/core/domain';

const CACHE_KEY = 'retornaar:user';
/** Lista completa de usuários (ex.: admin); separada do mapa de sessão para não confundir com getById/getUser. */
const ALL_USERS_LIST_CACHE_KEY = 'retornaar:users:all-list';

export interface UserLocalDataSource {
  getById(id: string): Promise<User | null>;
  /** Retorna o único usuário em cache (conta da sessão atual). Uma única entrada por sessão. */
  getUser(): Promise<User | null>;
  /** Lista em cache da última sincronização remota (getAll). */
  getAll(): Promise<User[]>;
  /** Substitui o cache da lista completa (resultado de getAll remoto). */
  cacheAll(users: User[]): Promise<void>;
  create(user: User): Promise<User>;
  update(id: string, user: Partial<User>): Promise<User>;
  delete(id: string): Promise<void>;
  clear(): Promise<void>;
}

const store = localforage.createInstance({ name: 'retornaar', storeName: 'user' });

export class UserLocalForageDataSource implements UserLocalDataSource {
  private async getMap(): Promise<Record<string, User>> {
    const raw = await store.getItem<Record<string, User>>(CACHE_KEY);
    return raw ?? {};
  }

  private async setMap(map: Record<string, User>): Promise<void> {
    await store.setItem(CACHE_KEY, map);
  }

  async getById(id: string): Promise<User | null> {
    const map = await this.getMap();
    return map[id] ?? null;
  }

  async getUser(): Promise<User | null> {
    const map = await this.getMap();
    const entries = Object.entries(map);
    if (entries.length === 0) return null;
    return entries[0][1];
  }

  async getAll(): Promise<User[]> {
    const raw = await store.getItem<User[]>(ALL_USERS_LIST_CACHE_KEY);
    return raw ?? [];
  }

  async cacheAll(users: User[]): Promise<void> {
    await store.setItem(ALL_USERS_LIST_CACHE_KEY, users);
  }

  async create(user: User): Promise<User> {
    const map = await this.getMap();
    map[user.id] = user;
    await this.setMap(map);
    return user;
  }

  async update(id: string, user: Partial<User>): Promise<User> {
    const map = await this.getMap();
    const current = map[id];
    if (!current) throw new Error('User not found');
    const updated = { ...current, ...user, id };
    map[id] = updated;
    await this.setMap(map);
    return updated;
  }

  async delete(id: string): Promise<void> {
    const map = await this.getMap();
    delete map[id];
    await this.setMap(map);
  }

  async clear(): Promise<void> {
    await store.removeItem(CACHE_KEY);
    await store.removeItem(ALL_USERS_LIST_CACHE_KEY);
  }
}
